import { isEqualWith } from 'lodash';
import {
  FreeEdgeLabelModel,
  FreeNodeLabelModel,
  IEdge,
  IGraph,
  ILabel,
  ILabelModelParameter,
  IModelItem,
  INode,
  Insets,
  InteriorNodeLabelModel,
  InteriorNodeLabelModelPosition,
  Point,
  Rect,
  Size,
} from '@ardoq/yfiles';
import { logError } from '@ardoq/logging';
import { ArdoqAddToScenario } from 'yfilesExtensions/styles/ArdoqAddToScenario';
import { DiffType, type LoadedScenarioData } from '@ardoq/data-model';
import {
  GraphItem,
  type GraphEdgeBase,
  formatOtherLabel,
  type YGraphState,
  isAggregatedGraphEdge,
  ComponentGraphNode,
  ComponentGraphGroup,
} from '@ardoq/graph';
import { groupEdges, DescendantCountLabelStyle } from '@ardoq/graph/yfiles';
import { createFifoCache, returnNull } from '@ardoq/common-helpers';
import { toIdSet } from '@ardoq/core';
import { GRAPH_ITEM_LABEL_CACHE_SIZE } from '@ardoq/global-consts';
import { ArdoqIcon } from '@ardoq/icons';
import { componentUtils } from '@ardoq/scope-data';
import {
  MODERNIZED_BLOCK_DIAGRAM_NODE_HEIGHT as NODE_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_NODE_WIDTH as NODE_WIDTH,
} from 'yfilesExtensions/styles/modernized/consts';
import { CanvasEdgeStyle } from './canvasEdge/CanvasEdgeStyle';
import { sizeEmptyGroupNodes } from '../../sizeEmptyGroupNodes';
import { ArdoqImageStyle } from 'yfilesExtensions/styles/modernized/ArdoqImageStyle';
import { ArdoqNodeStyle } from 'yfilesExtensions/styles/modernized/ArdoqNodeStyle';
import { GroupSizeConstraintProvider } from '../GroupSizeConstraintProvider';
import {
  INVISIBLE_SEPARATOR,
  MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING,
} from '../consts';
import { GroupSubLabelStyle } from '../groupLabels/GroupSubLabelStyle';
import type { ModernizedBlockDiagramViewSettings } from 'tabview/blockDiagram/types';
import { IconNodeStyle } from 'yfilesExtensions/styles/modernized/IconNodeStyle';
import { GroupStyle } from '../GroupStyle';
import { CollapsedComponentGroupLabelStyle } from '../CollapsedComponentGroupLabelStyle';
import {
  addAggregatedCountLabel,
  addGroupURLNodeLabel,
  addURLEdgeLabel,
  addURLNodeLabel,
} from './addGraphLabels';
import { updateEdgeLabels, updateLabels } from './updateGraphLabels';
import { isUrlLabel } from './isUrlLabel';
import { LeafNodeLabelStyle } from '../leafNodeLabel/LeafNodeLabelStyle';
import { LeafNodeOtherLabelStyle } from '../LeafNodeOtherLabelStyle';
import { GroupLabelStyle } from '../groupLabels/GroupLabelStyle';
import { GroupLabelModel } from '../groupLabels/GroupLabelModel';
import { OpenGroupOtherLabelStyle } from '../groupLabels/OpenGroupOtherLabelStyle';
import { OpenGroupOtherLabelModel } from '../groupLabels/OpenGroupOtherLabelModel';
import { GroupSubLabelModel } from '../groupLabels/GroupSubLabelModel';
import { CanvasLabelStyle } from '../../labels/CanvasLabelStyle';
import { CanvasOtherLabelStyle } from '../../labels/CanvasOtherLabelStyle';
import { layoutLeafNode } from './layoutLeafNode';
import { edgeOtherLabels } from './edgeOtherLabels';
import { edgeLabels } from './edgeLabels';
import { diffUpdateGraph } from 'yfilesExtensions/utils/diffUpdateGraph';

const getInitialLayout = (ancestor: INode | null, seedIndex: number) =>
  // if this is a descendant of a node which has been expanded, its origin (for animation purposes) should be the group center.
  ancestor
    ? new Rect(
        ancestor.layout.center.subtract(new Point(seedIndex, seedIndex)),
        Size.ZERO
      )
    : null;

const graphItemsEqual = (
  a: GraphItem | GraphItem[],
  b: GraphItem | GraphItem
) => !Array.isArray(a) && a.isEqual(b);

// #region label layout parameters
const addToScenarioButtonStyle = new ArdoqAddToScenario();
const addToScenarioNodeButtonModelParameter =
  FreeNodeLabelModel.INSTANCE.createParameter(
    new Point(0.5, 0), // place label at node layout top-center
    new Point(0, -5), // move up 5px
    new Point(0.5, 1) // anchor point of label is bottom-center
  );

const groupDescendantCountBadgeParameter = new InteriorNodeLabelModel({
  padding: new Insets(MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING),
}).createParameter(InteriorNodeLabelModelPosition.TOP_RIGHT);
const collapsedComponentGroupLabelLayoutParameter = InteriorNodeLabelModel.TOP;
const otherLabelsLayoutParameter = InteriorNodeLabelModel.TOP;
// #endregion

const leafNodeLayout = (graphNodes: ComponentGraphNode[]) =>
  new Map(
    graphNodes.map(graphNode => [graphNode.id, layoutLeafNode(graphNode)])
  );

export class YGraphBuilder {
  private shapeNodeStyle = createFifoCache(
    GRAPH_ITEM_LABEL_CACHE_SIZE,
    (shapeName: string) => new ArdoqNodeStyle(shapeName)
  );
  private imageNodeStyle = createFifoCache(
    GRAPH_ITEM_LABEL_CACHE_SIZE,
    (imageUrl: string) => new ArdoqImageStyle(imageUrl)
  );
  private iconNodeStyle = createFifoCache(
    GRAPH_ITEM_LABEL_CACHE_SIZE,
    (icon: ArdoqIcon) => new IconNodeStyle(icon)
  );
  private getNodeStyle = (
    graphNode: ComponentGraphNode,
    componentStyle: ModernizedBlockDiagramViewSettings['componentStyle']
  ) => {
    const representationData = graphNode.getRepresentationData();
    if (representationData?.isImage && representationData.value) {
      return this.imageNodeStyle(representationData.value);
    }
    if (componentStyle === 'icon') {
      const icon = representationData?.icon;
      if (icon) {
        return this.iconNodeStyle(icon);
      }
    }
    return this.shapeNodeStyle(graphNode.getShape());
  };
  private edgeMap = new Map<string, IEdge>();
  private nodeMap = new Map<string, INode>();
  private groupNodeMap = new Map<string, INode>();
  public componentStyle: ModernizedBlockDiagramViewSettings['componentStyle'] =
    'shape';
  public loadedScenarioData: LoadedScenarioData | null = null;
  private scenarioComponentIds: Set<string> = new Set();
  private connectedScenarioComponentIds: Set<string> = new Set();
  private openGroupOtherLabelsLayoutParameter: ILabelModelParameter;
  private groupSubLabelLayoutParameter: ILabelModelParameter;
  nodesSource: ComponentGraphNode[] = [];
  edgesSource: GraphEdgeBase[] = [];
  groupsSource: ComponentGraphGroup[] = [];

  constructor(private graph: IGraph) {
    const groupLabelModel = new GroupLabelModel(graph);
    const groupLabelLayoutParameter = groupLabelModel.createParameter();
    graph.groupNodeDefaults.labels.layoutParameter = groupLabelLayoutParameter;
    graph.groupNodeDefaults.size = new Size(1, 1);
    graph.edgeDefaults.style = CanvasEdgeStyle.Instance;

    // Style and labels
    graph.edgeDefaults.labels.style = CanvasLabelStyle.Instance;
    graph.nodeDefaults.labels.style = LeafNodeLabelStyle.Instance;
    graph.nodeDefaults.labels.layoutParameter = InteriorNodeLabelModel.CENTER;

    const edgeLabelModel = new FreeEdgeLabelModel();
    graph.edgeDefaults.labels.layoutParameter =
      edgeLabelModel.createParameter();

    this.openGroupOtherLabelsLayoutParameter = new OpenGroupOtherLabelModel(
      graph
    ).createParameter();
    this.groupSubLabelLayoutParameter = new GroupSubLabelModel(
      graph
    ).createParameter();
  }

  public updateScenarioData(loadedScenarioData: LoadedScenarioData | null) {
    this.loadedScenarioData = loadedScenarioData;
    if (loadedScenarioData?.scenarioData) {
      const { components, connected } = loadedScenarioData.scenarioData;
      this.scenarioComponentIds = toIdSet(components);
      this.connectedScenarioComponentIds = toIdSet(connected.components);
    } else {
      this.scenarioComponentIds.clear();
      this.connectedScenarioComponentIds.clear();
    }
  }

  private isScenarioRelated(componentId: string): boolean {
    return componentUtils.isComponentScenarioRelated(
      componentId,
      this.scenarioComponentIds,
      this.connectedScenarioComponentIds
    );
  }
  private createEdge = (graphEdge: GraphEdgeBase) => {
    const sourceNode =
      this.nodeMap.get(graphEdge.source) ||
      this.groupNodeMap.get(graphEdge.source);
    const targetNode =
      this.nodeMap.get(graphEdge.target) ||
      this.groupNodeMap.get(graphEdge.target);
    if (!sourceNode || !targetNode) {
      logError(Error('Source or Target node not found while creating Edge.'));
      return null;
    }

    const itemLabels = graphEdge.getItemLabels();
    const mainLabel = itemLabels?.mainLabel ?? '';
    const edge = this.graph.createEdge({
      tag: graphEdge,
      labels: [mainLabel],
      source: sourceNode,
      target: targetNode,
    });

    if (graphEdge.hasURLFields()) {
      addURLEdgeLabel(this.graph, edge, graphEdge, true);
    }
    if (isAggregatedGraphEdge(graphEdge)) {
      addAggregatedCountLabel(this.graph, edge, graphEdge);
    }
    const otherLabels = itemLabels?.otherLabels;
    if (!otherLabels?.length) {
      return edge;
    }
    const styles = otherLabels.map(l => l[3]);
    const { id } = graphEdge;
    this.graph.addLabel({
      owner: edge,
      text: otherLabels.map(formatOtherLabel).join(INVISIBLE_SEPARATOR),
      style: new CanvasOtherLabelStyle(styles),
      tag: (this.graph.tag as YGraphState).edgeOtherLabels.get(id) ?? null,
    });
    return edge;
  };
  private updateEdge = (graphEdge: GraphEdgeBase) => {
    const edge = this.edgeMap.get(graphEdge.id);
    if (!edge) {
      logError(Error('Edge not found in updateEdge.'));
      return null;
    }
    edge.tag = graphEdge; //  could be a new GraphEdge with the same id.
    /** is the sourcePort or targetPort gone? (group Node turned into a normal node or vice-versa) */
    const isEdgeMissingPort =
      !this.graph.contains(edge) || !edge.sourcePort || !edge.targetPort;
    if (isEdgeMissingPort) {
      return this.createEdge(graphEdge);
    }

    if (
      edge.sourceNode?.tag instanceof GraphItem &&
      edge.targetNode?.tag instanceof GraphItem
    ) {
      if (
        edge.sourceNode.tag.id !== graphEdge.source ||
        edge.targetNode.tag.id !== graphEdge.target
      ) {
        // if source or target has changed, we must create a new edge.
        this.removeEdge(edge);
        return this.createEdge(graphEdge);
      }
    }
    updateEdgeLabels(edge, this.graph, graphEdge, true);
    return edge;
  };

  private seedIndex = 0;
  private nextSeedIndex = (): number => this.seedIndex++;

  private getAncestorInGraph = (graphNode: GraphItem): INode | null =>
    (graphNode.parent &&
      (this.groupNodeMap.get(graphNode.parent.id) ??
        this.getAncestorInGraph(graphNode.parent))) ??
    null;

  private createNode = (graphNode: ComponentGraphNode) => {
    const labelText = graphNode.getItemLabels()?.mainLabel ?? '';
    const { id } = graphNode;
    const { tag: graphTag } = this.graph;
    const { leafNodeLayout } = graphTag as YGraphState;
    const wrappedLabel = leafNodeLayout.get(id)?.wrappedMainLabel ?? [];
    const node = this.graph.createNode({
      parent: graphNode.parent
        ? (this.groupNodeMap.get(graphNode.parent.id) ?? null)
        : null,
      tag: graphNode,
      labels: [
        {
          text: labelText,
          tag: wrappedLabel,
        },
      ],
      layout:
        getInitialLayout(
          this.getAncestorInGraph(graphNode),
          this.nextSeedIndex()
        ) ?? undefined,
      style: this.getNodeStyle(graphNode, this.componentStyle),
    });

    if (graphNode.hasURLFields()) {
      addURLNodeLabel(this.graph, node, graphNode);
    }
    if (
      !(graphNode.getVisualDiffType() === DiffType.PLACEHOLDER) &&
      this.isScenarioRelated(graphNode.modelId)
    ) {
      this.addToScenarioButton(node, graphNode);
    }
    this.addOtherNodeLabels(node, graphNode);
    this.updateNodeLayout(node);
    return node;
  };
  private updateNode = (graphNode: ComponentGraphNode) => {
    const node = this.nodeMap.get(graphNode.id)!;
    if (!node) {
      logError(Error('Node not found in updateNode.'));
      return null;
    }
    node.tag = graphNode; // could be a new GraphNode with the same ID.
    const style = this.getNodeStyle(graphNode, this.componentStyle);
    if (style !== node.style) {
      this.graph.setStyle(node, style);
    }
    this.updateNodeLayout(node);

    if (graphNode.hasURLFields() && !node.labels.find(isUrlLabel)) {
      addURLNodeLabel(this.graph, node, graphNode);
    }
    this.addOtherNodeLabels(node, graphNode);

    updateLabels(node, this.graph, graphNode);

    if (graphNode.parent) {
      const parentNode = this.groupNodeMap.get(graphNode.parent.id);
      if (parentNode) {
        this.graph.setParent(node, parentNode);
      } else {
        logError(Error('Parent node not found.'));
      }
    } else {
      this.graph.setParent(node, null);
    }
    if (
      !(graphNode.getVisualDiffType() === DiffType.PLACEHOLDER) &&
      this.isScenarioRelated(graphNode.modelId)
    ) {
      this.addToScenarioButton(node, graphNode);
    }
    return node;
  };

  private addGroupNodeLabels = (
    graphNode: ComponentGraphGroup,
    groupNode: INode
  ) => {
    const { collapsed, subLabel, descendantCount } = graphNode;
    const isCollapsedComponentGroup = collapsed && graphNode.isComponent();
    const labelText = graphNode.getItemLabels()?.mainLabel ?? '';
    this.graph.addLabel({
      owner: groupNode,
      text: labelText,
      style: isCollapsedComponentGroup
        ? CollapsedComponentGroupLabelStyle.Instance
        : GroupLabelStyle.Instance,
      layoutParameter: isCollapsedComponentGroup
        ? collapsedComponentGroupLabelLayoutParameter
        : undefined, // default to graph.groupNodeDefaults.labels.layoutParameter
    });
    if (!collapsed && subLabel) {
      this.graph.addLabel({
        owner: groupNode,
        text: subLabel,
        layoutParameter: this.groupSubLabelLayoutParameter,
        style: GroupSubLabelStyle.Instance,
      });
    }
    if (collapsed) {
      this.graph.addLabel({
        owner: groupNode,
        text: `${descendantCount}`,
        layoutParameter: groupDescendantCountBadgeParameter,
        style: DescendantCountLabelStyle.Instance,
      });
    }
    this.addOtherNodeLabels(groupNode, graphNode);
    if (graphNode.hasURLFields()) {
      addGroupURLNodeLabel(this.graph, groupNode, graphNode);
    }
  };
  private createGroupNode = (graphNode: ComponentGraphGroup) => {
    const groupNode = this.graph.createGroupNode({
      tag: graphNode,
      layout:
        getInitialLayout(
          this.getAncestorInGraph(graphNode),
          this.nextSeedIndex()
        ) ?? undefined,
      style: GroupStyle.Instance,
    });
    this.addGroupNodeLabels(graphNode, groupNode);
    return groupNode;
  };
  private updateGroupNode = (graphNode: ComponentGraphGroup) => {
    const groupNode = this.groupNodeMap.get(graphNode.id);
    if (!groupNode) {
      logError(Error('Group node not found.'));
      return null;
    }
    this.graph.setNodeLayout(
      groupNode,
      new Rect(groupNode.layout.center, new Size(1, 1))
    );
    this.graph.setStyle(groupNode, GroupStyle.Instance);
    groupNode.tag = graphNode; // this could be a new GraphGroup with the same id.
    groupNode.labels
      .toArray()
      .forEach((label: ILabel) => this.graph.remove(label));

    this.addGroupNodeLabels(graphNode, groupNode);
    return groupNode;
  };

  private removeItem = (item: IModelItem, map: Map<string, IModelItem>) => {
    const businessObject: GraphItem = item.tag;
    map.delete(businessObject.id);
    if (this.graph.contains(item)) {
      this.graph.remove(item);
    }
  };
  private removeEdge = (edge: IEdge) => {
    // Make sure the edge is attached to the graph
    if (this.graph.contains(edge) && (edge.sourcePort || edge.targetPort)) {
      this.removeItem(edge, this.edgeMap);
    }
  };

  hasLayoutUpdate({
    nodes,
    groups,
    edges,
  }: {
    nodes: ComponentGraphNode[];
    groups: ComponentGraphGroup[];
    edges: GraphEdgeBase[];
  }) {
    const nodesUnchanged =
      !nodes || isEqualWith(nodes, this.nodesSource, graphItemsEqual);
    const groupsUnchanged =
      !groups || isEqualWith(groups, this.groupsSource, graphItemsEqual);
    const edgesUnchanged =
      !edges || isEqualWith(edges, this.edgesSource, graphItemsEqual);

    return !(nodesUnchanged && groupsUnchanged && edgesUnchanged);
  }
  clearEdges() {
    Array.from(this.edgeMap.values()).forEach(this.removeEdge);
  }
  clear() {
    this.graph.clear();

    this.groupNodeMap.clear();
    this.nodeMap.clear();
    this.edgeMap.clear();
  }
  buildGraph() {
    this.clear();

    this.groupNodeMap = new Map(
      this.groupsSource.map(graphNode => [
        graphNode.id,
        this.createGroupNode(graphNode),
      ])
    );

    this.setGroupNodeParents();

    this.nodeMap = new Map(
      this.nodesSource.map(graphNode => [
        graphNode.id,
        this.createNode(graphNode),
      ])
    );

    sizeEmptyGroupNodes(this.graph, groupNode =>
      new GroupSizeConstraintProvider(groupNode).getMinimumSize()
    );

    this.edgeMap = new Map(
      this.edgesSource.map(graphEdge => {
        return [graphEdge.id, this.createEdge(graphEdge)!];
      })
    );
  }
  updateGraph() {
    this.seedIndex = 0;
    this.graph.tag = {
      edgeSourceGroups: new Map(),
      edgeSourceGroup: returnNull,
      edgeTargetGroups: new Map(),
      edgeTargetGroup: returnNull,
      leafNodeLayout: leafNodeLayout(this.nodesSource),
      groupHeaders: new Map(),
      edgeLabels: edgeLabels(this.edgesSource),
      edgeOtherLabels: edgeOtherLabels(this.edgesSource),
    } satisfies YGraphState;
    if (this.nodeMap.size + this.edgeMap.size + this.groupNodeMap.size === 0) {
      this.buildGraph();
      this.graph.tag = {
        ...this.graph.tag,
        ...groupEdges(this.graph),
        groupHeaders: new Map(),
      } satisfies YGraphState;
      return;
    }
    this.groupNodeMap = diffUpdateGraph({
      oldMap: this.groupNodeMap,
      businessObjects: this.groupsSource,
      create: this.createGroupNode,
      update: this.updateGroupNode,
      remove: this.removeItem,
    });

    this.setGroupNodeParents();

    this.nodeMap = diffUpdateGraph<ComponentGraphNode, INode>({
      oldMap: this.nodeMap,
      businessObjects: this.nodesSource,
      create: this.createNode,
      update: this.updateNode,
      remove: this.removeItem,
    });

    sizeEmptyGroupNodes(this.graph, groupNode =>
      new GroupSizeConstraintProvider(groupNode).getMinimumSize()
    );

    this.edgeMap = diffUpdateGraph({
      oldMap: this.edgeMap,
      businessObjects: this.edgesSource,
      create: this.createEdge,
      update: this.updateEdge,
      remove: this.removeEdge,
    });

    this.graph.tag = {
      ...this.graph.tag,
      ...groupEdges(this.graph),
      groupHeaders: new Map(),
    } satisfies YGraphState;
  }

  private removeOtherNodeLabels(owner: INode) {
    const labelsToRemove = owner.labels
      .filter(
        ({ style }) =>
          style instanceof LeafNodeOtherLabelStyle ||
          style instanceof OpenGroupOtherLabelStyle
      )
      .toArray();
    labelsToRemove.forEach(labelToRemove => this.graph.remove(labelToRemove));
  }
  private addOtherNodeLabels(
    owner: INode,
    graphNode: ComponentGraphNode | ComponentGraphGroup
  ) {
    this.removeOtherNodeLabels(owner);

    const itemLabels = graphNode.getItemLabels();
    if (!itemLabels) {
      return;
    }
    const { otherLabels } = itemLabels;
    if (!otherLabels?.length) {
      return;
    }

    const isOpenGroup =
      graphNode instanceof ComponentGraphGroup && !graphNode.collapsed;

    const labelStyleInstance = isOpenGroup
      ? new OpenGroupOtherLabelStyle()
      : new LeafNodeOtherLabelStyle();

    const layoutParameter = isOpenGroup
      ? this.openGroupOtherLabelsLayoutParameter
      : otherLabelsLayoutParameter;
    this.graph.addLabel({
      owner,
      text: otherLabels.map(formatOtherLabel).join(INVISIBLE_SEPARATOR),
      layoutParameter,
      style: labelStyleInstance,
      tag: isOpenGroup ? null : graphNode,
    });
  }
  private setGroupNodeParents() {
    // Set parents of group nodes
    Array.from(this.groupNodeMap.values()).forEach(groupNode => {
      const parent =
        groupNode.tag instanceof GraphItem &&
        groupNode.tag.parent &&
        this.groupNodeMap.get(groupNode.tag.parent.id);

      if (parent) {
        this.graph.setParent(groupNode, parent);
      }
    });
  }
  private updateNodeLayout(node: INode) {
    const {
      tag,
      layout: {
        x,
        center: { y: centerY },
      },
    } = node;
    const graphNode = tag as ComponentGraphNode;
    const { id } = graphNode;
    this.graph.setStyle(
      node,
      this.getNodeStyle(graphNode, this.componentStyle)
    );
    const { tag: graphTag } = this.graph;
    const { leafNodeLayout } = graphTag as YGraphState;
    const nodeHeight = leafNodeLayout.get(id)?.nodeHeight ?? NODE_HEIGHT;
    this.graph.setNodeLayout(
      node,
      new Rect(x, centerY - nodeHeight / 2, NODE_WIDTH, nodeHeight)
    );
  }
  private addToScenarioButton(owner: INode, graphNode: GraphItem) {
    this.graph.addLabel({
      owner,
      text: '',
      layoutParameter: addToScenarioNodeButtonModelParameter,
      style: addToScenarioButtonStyle,
      tag: graphNode,
    });
  }
}
